import React from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

import { Link } from 'ui/base/link';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page/standard_page';
import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import { BasePath } from 'services/base/page_path';
import { Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import { themedModuleIconClassStyles } from 'assets/enums/modules/modules';

import baseStyles from './view_pangolin.module.css';
import ViewPangolinMessages from './view_pangolin_messages';

type ViewPangolinStyles = {
  body: string;
  link: string;
  column: string;
  textLabel: string;
  subtitleText: string;
  shieldContainer: string;
  editPangolinContainer: string;
  secondaryLinksContainer: string;
  row: string;
  icon: string;
  interceptIcon: string;
  scannerIcon: string;
  superUserIcon: string;
  pangolinRow: string;
  pangolinDevice: string;
};

const themedStyles: ThemedStyles<ViewPangolinStyles> = {
  [Theme.DAYLIGHT]: {
    body: baseStyles.baseBody,
    link: baseStyles.baseLink,
    column: baseStyles.baseColumn,
    textLabel: baseStyles.daylightTextLabel,
    subtitleText: baseStyles.daylightSubtitleText,
    shieldContainer: baseStyles.baseShieldContainer,
    editPangolinContainer: baseStyles.editPangolinContainer,
    secondaryLinksContainer: baseStyles.secondaryLinksContainer,
    row: baseStyles.row,
    icon: baseStyles.baseIcon,
    interceptIcon: baseStyles.daylightInterceptIcon,
    scannerIcon: baseStyles.daylightScannerIcon,
    superUserIcon: baseStyles.daylightSuperUserIcon,
    pangolinRow: baseStyles.pangolinRow,
    pangolinDevice: baseStyles.daylightPangolinDevice,
  },
  [Theme.MIDNIGHT]: {
    body: baseStyles.baseBody,
    link: baseStyles.baseLink,
    column: baseStyles.baseColumn,
    textLabel: baseStyles.midnightTextLabel,
    subtitleText: baseStyles.midnightSubtitleText,
    shieldContainer: baseStyles.baseShieldContainer,
    editPangolinContainer: baseStyles.editPangolinContainer,
    secondaryLinksContainer: baseStyles.secondaryLinksContainer,
    row: baseStyles.row,
    icon: baseStyles.baseIcon,
    interceptIcon: baseStyles.midnightInterceptIcon,
    scannerIcon: baseStyles.midnightScannerIcon,
    superUserIcon: baseStyles.midnightSuperUserIcon,
    pangolinRow: baseStyles.pangolinRow,
    pangolinDevice: baseStyles.midnightPangolinDevice,
  },
};

export const BaseViewPangolin = observer(({ pangolin }: { pangolin: Pangolin }) => {
  const styles = useStyles(themedStyles);
  const moduleIcons = useStyles(themedModuleIconClassStyles);
  return (
    <StandardPage>
      <div className={styles.body}>
        <BackButton />
        <div className={styles.editPangolinContainer}>
          <Link path={{ kind: BasePath.MANAGE_PANGOLIN_EDIT, pangolinID: pangolin.id }} className={styles.link}>
            <div className={styles.pangolinRow}>
              <div className={styles.column}>
                <div className={styles.textLabel}>
                  {pangolin.nickname}
                </div>
                <div className={styles.subtitleText}>
                  {ViewPangolinMessages.edit()}
                </div>
              </div>
              <div className={styles.shieldContainer}>
                <ShieldIcon colorCode={pangolin.colorCode} onlineStatus={pangolin.onlineStatus} size={ShieldSize.MEDIUM}/>
              </div>
            </div>
          </Link>
        </div>
        <div className={styles.secondaryLinksContainer}>
          <Link path={{ kind: BasePath.DEVICES_INFO, pangolinID: pangolin.id }} className={styles.link}>
            <div className={styles.row}>
              <div>
                <div className={classNames(styles.icon, moduleIcons.devices)} />
              </div>
              <div className={styles.textLabel}>
                {ViewPangolinMessages.deviceInformation()}
              </div>
            </div>
          </Link>
          <Link path={{ kind: BasePath.HISTORY_LIST, pangolinID: pangolin.id }} className={styles.link}>
            <div className={styles.row}>
              <div>
                <div className={classNames(styles.icon, moduleIcons.history)} />
              </div>
              <div className={styles.textLabel}>
                {ViewPangolinMessages.trafficHistory()}
              </div>
            </div>
          </Link>
          <Link path={{ kind: BasePath.BANDWIDTH_LIST, pangolinID: pangolin.id }} className={styles.link}>
            <div className={styles.row}>
              <div>
                <div className={classNames(styles.icon, moduleIcons.bandwidth)} />
              </div>
              <div className={styles.textLabel}>
                {ViewPangolinMessages.bandwidthPrioritization()}
              </div>
            </div>
          </Link>
          <Link path={{ kind: BasePath.SCHEDULE_LIST, pangolinID: pangolin.id }} className={styles.link}>
            <div className={styles.row}>
              <div>
                <div className={classNames(styles.icon, moduleIcons.schedules)} />
              </div>
              <div className={styles.textLabel}>
                {ViewPangolinMessages.parentalControls()}
              </div>
            </div>
          </Link>
          <Link path={{ kind: BasePath.VPN_LIST, pangolinID: pangolin.id }} className={styles.link}>
            <div className={styles.row}>
              <div>
                <div className={classNames(styles.icon, moduleIcons.vpnserver)} />
              </div>
              <div className={styles.textLabel}>
                {ViewPangolinMessages.vpnServer()}
              </div>
            </div>
          </Link>
          <Link path={{ kind: BasePath.MANAGE_PANGOLIN_INTERCEPT, pangolinID: pangolin.id }} className={styles.link}>
            <div className={styles.row}>
              <div>
                <div className={styles.interceptIcon} />
              </div>
              <div className={styles.textLabel}>
                {ViewPangolinMessages.interceptTuning()}
              </div>
            </div>
          </Link>
          <Link path={{ kind: BasePath.MANAGE_PANGOLIN_SCANNER, pangolinID: pangolin.id }} className={styles.link}>
            <div className={styles.row}>
              <div>
                <div className={styles.scannerIcon} />
              </div>
              <div className={styles.textLabel}>
                {ViewPangolinMessages.scannerTuning()}
              </div>
            </div>
          </Link>
          <Link path={{ kind: BasePath.MANAGE_PANGOLIN_UNLINK, pangolinID: pangolin.id }} className={styles.link}>
            <div className={styles.row}>
              <div>
                <div className={styles.superUserIcon} />
              </div>
              <div className={styles.textLabel}>
                {ViewPangolinMessages.superUser()}
              </div>
            </div>
          </Link>
        </div>
      </div>
    </StandardPage>
  );
});

export const ViewPangolin = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  return <BaseViewPangolin pangolin={pangolin}/>;
});
